import { css } from '@styled-system/css';
import type { FC } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';

import { useAnalysisStatistics } from '@/features/analysisPageStatistics/hooks/useAnalysisStatistics';
import { formatNumber, formatPercentage, formatPrice } from '@/shared/utils/format';

const strip = css({
  display: 'flex',
  alignItems: 'stretch',
  flexWrap: 'wrap',
  gap: '8',
  paddingY: '4',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
});

const item = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1',
  minWidth: '120px',
});

const itemLabel = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  whiteSpace: 'nowrap',
});

const itemValue = css({
  fontSize: 'h3',
  lineHeight: 'h3',
  fontWeight: 'bold',
  color: 'text.primary',
  fontFamily: 'sans',
});

const itemValueAccent = css({
  fontSize: 'h3',
  lineHeight: 'h3',
  fontWeight: 'bold',
  color: 'brand.primary',
  fontFamily: 'sans',
});

interface AnalysisKeyStatsStripProps {
  analysisId: string;
}

/**
 * Bandeau compact des métriques clés, affiché au-dessus des onglets pour les
 * recherches larges (intention « découverte ») : l'utilisateur atterrit sur
 * les niches sans passer par l'onglet Statistiques, ce bandeau garde les
 * chiffres transverses visibles.
 */
export const AnalysisKeyStatsStrip: FC<AnalysisKeyStatsStripProps> = ({ analysisId }) => {
  const { t } = useTranslation();
  const { data, loading, error } = useAnalysisStatistics({ analysisId });

  if (error) return null;

  const stats: { key: string; label: string; value: string; accent?: boolean }[] = [
    {
      key: 'salePct',
      label: t('keyStats.salePct'),
      value: formatPercentage(data?.salePercentage ?? null),
      accent: true,
    },
    {
      key: 'avgPrice',
      label: t('keyStats.avgPrice'),
      value: formatPrice(data?.averageSalePrice ?? null),
    },
    {
      key: 'totalSales',
      label: t('keyStats.totalSales'),
      value: formatNumber(data?.totalSalesRecorded ?? null),
    },
    {
      key: 'totalProducts',
      label: t('keyStats.totalProducts'),
      value: formatNumber(data?.totalProductsAnalyzed ?? null),
    },
  ];

  return (
    <div className={strip}>
      {stats.map((stat) => (
        <div key={stat.key} className={item}>
          <span className={itemLabel}>{stat.label}</span>
          <span className={stat.accent ? itemValueAccent : itemValue}>
            {loading ? <Skeleton width={64} /> : stat.value}
          </span>
        </div>
      ))}
    </div>
  );
};
